/**
 * Генерация PDF-спецификации для отправки в цех.
 * Конвертирует HTML в PDF через Python-бэкенд (WeasyPrint).
 * Fallback: открывает HTML в новой вкладке с автопечатью.
 */
import fittingsData from '../components/fittingsData';
import { facingOutletBomParts, pricedNippelMuftaLength } from '../components/AutoConstructor/collectorUtils';
import { formatFittingDisplayName } from './fittingDisplayName';
import { persistBlob } from './fileSystemSave';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ─── Группировка данных ───────────────────────────────────────────────────────

function buildRows(objectsSnapshot) {
  const groups = new Map();

  (objectsSnapshot.fittings || []).forEach((f) => {
    if (f.type === 'fastener' && f.itemKey === 'KRGB') {
      const profileLength = f.fastenerHeight || f.height || 400;
      const profileKey = 'PROFILE_80x40_KRGB';
      if (!groups.has(profileKey)) {
        groups.set(profileKey, { key: profileKey, qty: 0, sumLength: 0,
          displayName: 'Профиль 80×40 (для KRGB)', isKRGB: true });
      }
      groups.get(profileKey).qty += 4;
      groups.get(profileKey).sumLength += profileLength * 4;

      const pipeDiameter = f.pipeDiameter || f.width || 406.4;
      const pipeKey = `PIPE_KRGB_${pipeDiameter}`;
      if (!groups.has(pipeKey)) {
        groups.set(pipeKey, { key: pipeKey, qty: 0, sumLength: 0,
          displayName: `Труба Ø${pipeDiameter} (для KRGB)`, isKRGB: true });
      }
      groups.get(pipeKey).qty += 1;
      groups.get(pipeKey).sumLength += 120;
      return;
    }
    if (f.type === 'fastener') return;
    if (f.isHidden) return;

    const itemKey = f.itemKey || 'UNKNOWN';
    const info = fittingsData[itemKey];
    const category = info?.category || '';
    const isMufta  = category === 'Муфты';
    const isNippel = category === 'Ниппели';
    const isNippelOrMufta = isNippel || isMufta;

    const priced = (isNippel || isMufta)
      ? pricedNippelMuftaLength(f, objectsSnapshot.cylinders)
      : null;
    const length = priced != null
      ? priced
      : (f.isSelfFacing
        ? Number(info?.length || f.height || 0)
        : Number(f.height || 0));

    const roundedLength = Math.round(length * 100) / 100;
    const key = isNippelOrMufta ? `${itemKey}__len_${roundedLength}` : itemKey;

    if (!groups.has(key)) {
      groups.set(key, {
        key, itemKey, qty: 0, sumLength: 0,
        isNippelOrMufta, isMufta, isNippel,
        unitLength: isNippelOrMufta ? roundedLength : null,
        displayName: formatFittingDisplayName(itemKey, info),
        category,
      });
    }
    const g = groups.get(key);
    g.qty += 1;
    g.sumLength += length;

    facingOutletBomParts(f, objectsSnapshot.cylinders).forEach((part) => {
      if (part.isPipe) {
        const pipeKey = `PIPE_ROUND_${part.diameter}_${part.thickness}_${part.coating}__len_${part.length}`;
        if (!groups.has(pipeKey)) {
          groups.set(pipeKey, {
            key: pipeKey,
            itemKey: pipeKey,
            qty: 0,
            sumLength: 0,
            isPipe: true,
            displayName: `Труба Ø${part.diameter} ${part.thickness} ${part.coating}`,
            unitLength: part.length,
          });
        }
        const pipeGroup = groups.get(pipeKey);
        pipeGroup.qty += 1;
        pipeGroup.sumLength += part.length;
        return;
      }
      const extraInfo = fittingsData[part.itemKey];
      if (!groups.has(part.itemKey)) {
        groups.set(part.itemKey, {
          key: part.itemKey,
          itemKey: part.itemKey,
          qty: 0,
          sumLength: 0,
          displayName: formatFittingDisplayName(part.itemKey, extraInfo),
          category: extraInfo?.category || '',
        });
      }
      const extra = groups.get(part.itemKey);
      extra.qty += 1;
      extra.sumLength += Number(extraInfo?.length) || 0;
    });
  });

  (objectsSnapshot.cylinders || []).forEach((c) => {
    if (c.isHidden) return;
    const ps = c.pipeSpec;

    if (ps && ps.shape === 'round' && typeof ps.diameter === 'number') {
      const diamKey = String(ps.diameter);
      const thick   = ps.thickness || '-';
      const coating = ps.coating || '-';
      const dw = Math.abs((c.width || 0) - Number(ps.diameter));
      const dh = Math.abs((c.height || 0) - Number(ps.diameter));
      const geomLen  = dw <= dh ? (c.height || 0) : (c.width || 0);
      const runLength = Math.round((geomLen + (c.insertionDepth || 0)) * 100) / 100;
      const baseKey = `PIPE_ROUND_${diamKey}_${thick}_${coating}`;
      const key = `${baseKey}__len_${runLength}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key, itemKey: baseKey, qty: 0, sumLength: 0,
          isPipe: true, unitLength: runLength,
          displayName: `Труба Ø${diamKey} (${thick}, ${coating})`,
        });
      }
      const g = groups.get(key);
      g.qty += 1;
      g.sumLength += runLength;
    }

    if (ps && ps.shape === 'square' && ps.profile) {
      const profile   = String(ps.profile);
      const thick     = ps.thickness || '-';
      const coating   = ps.coating || '-';
      const sqType    = ps.squareType || 'profile';
      const runLength = Math.round(Math.max(c.width || 0, c.height || 0) * 100) / 100;
      const baseKey = `PIPE_SQUARE_${sqType.toUpperCase()}_${profile}_${thick}_${coating}`;
      const key = `${baseKey}__len_${runLength}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key, itemKey: baseKey, qty: 0, sumLength: 0,
          isPipe: true, unitLength: runLength,
          displayName: sqType === 'sheet'
            ? `Лист ${profile} (${thick}, ${coating})`
            : `Профиль ${profile} (${thick}, ${coating})`,
        });
      }
      const g = groups.get(key);
      g.qty += 1;
      g.sumLength += runLength;
    }
  });

  const result = Array.from(groups.values());
  result.sort((a, b) => {
    const ak = a.itemKey || a.key;
    const bk = b.itemKey || b.key;
    if (ak !== bk) return ak < bk ? -1 : 1;
    return (a.unitLength || 0) - (b.unitLength || 0);
  });
  return result;
}

function formatLen(row) {
  // Дополнительные строки — показываем длину если задана
  if (row.isExtra) {
    return row.sumLength > 0 ? `${row.sumLength} мм` : '—';
  }
  // Трубы — длина одной трубы + единица
  if (row.isPipe) {
    const len = row.unitLength != null
      ? row.unitLength
      : Math.round(row.sumLength * 100) / 100;
    return `${len} мм`;
  }
  // Ниппели и муфты — длина одного фитинга + единица
  if (row.isNippel || row.isMufta) {
    return row.unitLength != null ? `${row.unitLength} мм` : '—';
  }
  // Все остальные фитинги (заглушки, переходы, фланцы и т.д.) — прочерк
  return '—';
}

// ─── HTML ─────────────────────────────────────────────────────────────────────

function buildHtml(rows, artikul, today) {
  const trs = rows.map((r, i) => `
    <tr${i % 2 === 1 ? ' class="alt"' : ''}>
      <td class="c">${i + 1}</td>
      <td>${escHtml(r.displayName || r.key)}</td>
      <td class="c">${r.qty}</td>
      <td class="c">${formatLen(r)}</td>
    </tr>`).join('');

  // Два варианта CSS: для браузера (экран) и для PDF-рендерера (xhtml2pdf не поддерживает flex/position:fixed)
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<title>Спецификация ${escHtml(artikul)}</title>
<style>
/* Общие стили */
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #1e1e1e; background: #fff; }

/* Шапка — таблица вместо flex для совместимости с xhtml2pdf */
.hbar { background: #b41e1e; color: #fff; width: 100%; border-collapse: collapse; margin-bottom: 0; -pdf-keep-with-next: true; }
.hbar td { padding: 7px 18px; color: #fff; font-size: 10pt; }
.hbar .logo { font-weight: bold; font-size: 11pt; }
.hbar .dt { text-align: right; font-size: 9pt; }

/* Заголовок */
.title { padding: 12px 18px 8px; border-bottom: 2.5px solid #b41e1e; margin-bottom: 12px; text-align: center; }
.title h1 { font-size: 14pt; font-weight: bold; }

/* Таблица */
.wrap { padding: 0 18px; }
table.spec { width: 100%; border-collapse: collapse; }
table.spec col.n  { width: 30px; }
table.spec col.nm { width: auto; }
table.spec col.q  { width: 55px; }
table.spec col.l  { width: 80px; }
table.spec thead tr { background: #282828; color: #fff; }
table.spec thead th { padding: 6px 7px; font-size: 9pt; font-weight: bold; text-align: center; color: #fff; border: 1px solid #444; }
table.spec thead th.left { text-align: left; }
table.spec tbody tr { border-bottom: 1px solid #ddd; }
table.spec tbody tr.alt { background: #f2f2f2; }
table.spec tbody td { padding: 5px 7px; font-size: 9pt; border-bottom: 1px solid #e0e0e0; }
td.c { text-align: center; }
td.r { text-align: right; }

/* Нижняя подпись */
.finfo { padding: 10px 18px 4px; font-size: 8pt; color: #666; }
.fbar-bottom { background: #b41e1e; color: #fff; width: 100%; border-collapse: collapse; margin-top: 8px; }
.fbar-bottom td { padding: 5px 18px; color: #fff; font-size: 8pt; text-align: center; }

/* Только для браузера */
@media screen {
  .hbar, .fbar-bottom { display: table; }
}
@page { margin: 12mm 10mm 12mm; }
</style>
</head>
<body>

<table class="hbar" width="100%">
  <tr>
    <td class="logo">ProxythermCAD</td>
    <td class="dt">${today}</td>
  </tr>
</table>

<div class="title"><h1>Спецификация для &#171;${escHtml(artikul)}&#187;</h1></div>

<div class="wrap">
<table class="spec">
<colgroup><col class="n"><col class="nm"><col class="q"><col class="l"></colgroup>
<thead><tr>
  <th>&#8470;</th>
  <th class="left">Наименование</th>
  <th>Кол-во</th>
  <th>Длина</th>
</tr></thead>
<tbody>${trs}</tbody>
</table>
</div>

<div class="finfo">Позиций: ${rows.length} &nbsp;&nbsp;&nbsp; Сформировано: ${today}</div>

<table class="fbar-bottom" width="100%">
  <tr><td>ProxythermCAD &nbsp;&#124;&nbsp; ${escHtml(artikul)}</td></tr>
</table>

</body>
</html>`;
}

function escHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ─── Публичная функция ────────────────────────────────────────────────────────

/**
 * Генерирует спецификацию и сохраняет HTML-файл.
 * Если передан directoryHandle (от IGES-экспортера) — сохраняет туда напрямую.
 * Иначе использует showSaveFilePicker или a.download как fallback.
 * @param {Object} objectsSnapshot
 * @param {string} artikul
 * @param {FileSystemDirectoryHandle|null} directoryHandle
 */
/**
 * Генерирует спецификацию как PDF через бэкенд (WeasyPrint).
 * Если бэкенд недоступен — открывает HTML с автопечатью в новой вкладке.
 * Если передан directoryHandle — сохраняет PDF туда без диалога.
 * @param {Object} objectsSnapshot
 * @param {string} artikul
 * @param {FileSystemDirectoryHandle|null} directoryHandle
 * @param {Object|null} calculationSettings — настройки цены с overrides и extra-строками
 */
export async function generateSpecificationPdf(
  objectsSnapshot,
  artikul,
  directoryHandle = null,
  calculationSettings = null,
  saveContext = {},
) {
  const { fileHandle: presetFileHandle = null } = saveContext;
  const dirHandle = directoryHandle || saveContext.directoryHandle || null;
  const fileHandle = presetFileHandle;
  const componentRowOverrides = calculationSettings?.componentRowOverrides || {};
  const extraComponentRows = calculationSettings?.extraComponentRows || [];

  // Применяем overrides к строкам из чертежа
  let rows = buildRows(objectsSnapshot)
    .filter(r => !(componentRowOverrides[r.key]?.hidden))
    .map(r => {
      const ov = componentRowOverrides[r.key];
      if (!ov) return r;
      const effQty = ov.qty != null ? Number(ov.qty) : r.qty;
      return { ...r, qty: effQty };
    });

  // Добавляем дополнительные строки
  const extraRows = extraComponentRows.map(er => ({
    key: er.id,
    displayName: er.name || '—',
    qty: Number(er.qty) || 0,
    sumLength: Number(er.length) || 0,
    unitLength: Number(er.length) || null,
    isExtra: true,
  }));
  rows = [...rows, ...extraRows];

  if (!rows.length) {
    console.warn('Спецификация: нет данных');
    return;
  }

  const today    = new Date().toLocaleDateString('ru-RU');
  const html     = buildHtml(rows, artikul, today);
  const safeName = artikul.replace(/[<>:"/\\|?*]/g, '_').trim() || 'Спецификация';
  const pdfName  = `Спецификация ${safeName}.pdf`;

  // Пробуем сгенерировать PDF через бэкенд
  try {
    const response = await fetch(`${BACKEND_URL}/generate-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, filename: pdfName }),
    });

    if (response.ok) {
      const pdfBlob = await response.blob();

      await persistBlob(pdfBlob, pdfName, { directoryHandle: dirHandle, fileHandle });
      console.log('✅ Спецификация сохранена:', pdfName);
      return;
    }
    console.warn('Бэкенд вернул ошибку, fallback на HTML-печать');
  } catch (err) {
    console.warn('Бэкенд недоступен, fallback на HTML-печать:', err.message);
  }

  // Fallback: открываем HTML в новой вкладке — браузер предложит печать
  const htmlBlob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const printUrl = URL.createObjectURL(htmlBlob);
  const win = window.open(printUrl, '_blank');
  if (win) {
    win.addEventListener('load', () => URL.revokeObjectURL(printUrl), { once: true });
  } else {
    URL.revokeObjectURL(printUrl);
    // Если popup заблокирован — скачиваем HTML
    const htmlName = `Спецификация ${safeName}.html`;
    await persistBlob(htmlBlob, htmlName, { directoryHandle: dirHandle, fileHandle });
    console.log('✅ Спецификация сохранена:', htmlName);
  }
}
